import React from 'react';
import { findDOMNode } from 'react-dom';
import { fromJS, List } from 'immutable';
import { NavLink } from 'fluxible-router';
import DelectableRating from '../DelectableRating';
import Image from '../Image';
import shallowCompare from '../../lib/shallowCompare';
import { toTitleCase } from '../../lib/utils';
import ShoppingCartActions from '../../actions/ShoppingCartActions';
import BookmarkActions from '../../actions/BookmarkActions';
import CollectionStore from '../../stores/CollectionStore';
import AttributedText from '../AttributedText';
import ZipCodeModal from '../ZipCodeModal';
import Select from '../Select';
import SimilarProductsSection from '../SimilarProductsSection';
import InventoryRecordReview from '../InventoryRecordReview';
import QuantitySelector from '../QuantitySelector';
import Collection from '../../models/Collection';
import Customer from '../../models/Customer';
import InventoryRecord, { Badge } from '../../models/InventoryRecord';
import OrderItem from '../../models/OrderItem';
import Store from '../../models/Store';
import { RequestStates } from '../../stores/PaginatedStore';
import facebookPng from './images/facebook.png';
import facebookSvg from './images/facebook.svg';
import twitterPng from './images/twitter.png';
import twitterSvg from './images/twitter.svg';
import emailPng from './images/email.png';
import emailSvg from './images/email.svg';
import delectablePng from './images/logo_delectable_mark_2x.png';
import HeartPng from './images/heart_filled.png';
import RedHeartPng from './images/heart_filled_highlight.png';
import HeartSvg from './images/heart_filled.svg';
import RedHeartSvg from './images/heart_filled_highlight.svg';
import styles from './index.sass';

const ENV = (typeof window !== 'undefined' ? window.ENV : (process.env.NODE_ENV || 'development'));
const ZipCodeProtectedActions = {
  AddToCart: 'addToCart',
  Bookmark: 'bookmark'
};
const InitialNumReviewsToShow = 10;

class InventoryRecordDetail extends React.Component {
  static propTypes = {
    collection: React.PropTypes.instanceOf(Collection),
    customer: React.PropTypes.instanceOf(Customer),
    inventoryRecord: React.PropTypes.instanceOf(InventoryRecord),
    store: React.PropTypes.instanceOf(Store),
    similarProductCategories: React.PropTypes.instanceOf(List),
    similarProductCategoriesRequestStatus: React.PropTypes.string
  };

  static contextTypes = {
    executeAction: React.PropTypes.func,
    getStore: React.PropTypes.func,
    getUserAgent: React.PropTypes.func,
    buildURLForRoute: React.PropTypes.func
  };

  constructor(props) {
    super(props);

    this.state = {
      showZipCodeModal: false,
      attemptedAction: null,
      showAllReviews: false
    };
  }

  shouldComponentUpdate(nextProps, nextState) {
    return shallowCompare(this, nextProps, nextState);
  }

  componentWillReceiveProps(nextProps, nextState) {
    if(nextProps.inventoryRecord.id !== this.props.inventoryRecord.id) {
      this.setState({showAllReviews: false});
    }
  }

  bookmark() {
    let customerContext = this.props.inventoryRecord.customer_context;
    let bookmarked = customerContext ? customerContext.bookmarked : false;
    let action = !bookmarked ? BookmarkActions.create : BookmarkActions.delete;
    let bookmarkMetadata = {};
    if(this.props.currentRoute.params.collectionSlug && this.props.collection) {
      bookmarkMetadata.collectionId = this.props.collection.id;
    }
    this.context.executeAction(action, _.extend({inventoryRecord: this.props.inventoryRecord}, bookmarkMetadata));
  }

  handleOnClickBuyButton(e) {
    let customer = this.props.customer;
    if(!customer || !customer.preferences.zip_code) {
      this.setState({showZipCodeModal: true, attemptedAction: ZipCodeProtectedActions.AddToCart});
    } else {
      this.addToCart();
    }
  }

  handleOnClickHeartButton(e) {
    let customer = this.props.customer;
    if(!customer || !customer.preferences.zip_code) {
      this.setState({showZipCodeModal: true, attemptedAction: ZipCodeProtectedActions.Bookmark});
    } else {
      this.bookmark();
    }
  }

  handleOnCloseZipCodeModal(zipCodeAdded) {
    this.setState({showZipCodeModal: false});
    if(!zipCodeAdded) {return;}
    if(this.state.attemptedAction) {
      this[this.state.attemptedAction]();
      this.setState({attemptedAction: null});
    }
  }

  handleOnClickShowAllReviewsButton(e) {
    this.setState({showAllReviews: true});
  }

  renderZipCodeModal() {
    if(!this.state.showZipCodeModal) {return null;}
    let zipCodeModalProps = {
      onClose: this.handleOnCloseZipCodeModal.bind(this)
    };
    return <ZipCodeModal {...zipCodeModalProps} />;
  }

  renderPrice() {
    let priceText = this.props.inventoryRecord.price_text;
    let salePriceText = this.props.inventoryRecord.sale_price_text;
    if(!salePriceText) {
      return (
        <div className={styles.price}>
          <span>{priceText}</span>
        </div>
      );
    }
    return (
      <div className={styles.price}>
        <span className={styles.strikethrough}>{priceText}</span>
        <span>{salePriceText}</span>
      </div>
    );
  }

  renderHeartButton() {
    let customerContext = this.props.inventoryRecord.customer_context;
    let bookmarked = customerContext ? customerContext.bookmarked : false;
    let heartButtonProps = {
      className: styles.heartButton,
      onClick: this.handleOnClickHeartButton.bind(this)
    };
    return (
      <button {...heartButtonProps}>
        <img src={bookmarked ? RedHeartPng : HeartPng} srcSet={bookmarked ? RedHeartSvg : HeartSvg} />
        <span>{bookmarked ? 'Saved' : 'Save'}</span>
      </button>
    );
  }

  renderRatings(ratings) {
    if(!ratings) {return null;}
    // For testing...
    // ratings = [{
    //   type: 'delectable',
    //   object: {
    //     ratings_count: 62,
    //     average_rating: 9.3,
    //     pro_ratings_count: 12,
    //     average_pro_rating: 9.1
    //   }
    // }];
    return ratings.map((rating, i) => {
      switch(rating.type) {
        case 'delectable':
          let delectableRatingProps = {
            key: i,
            rating: rating.object.average_rating,
            count: rating.object.ratings_count,
            style: {display: 'block', whiteSpace: 'pre'}
          };
          return <DelectableRating {...delectableRatingProps} />;
          break;
        default:
          break;
      }
    });
  }

  renderReviews(inventoryRecord) {
    let reviews = inventoryRecord.reviews;
    if(!reviews) {return null;}
    let { showAllReviews } = this.state; // Shows all for all types
    return reviews.map((obj, i) => {
      // NOTE: Only 'delectable' type reviews are syupported as of now
      if(obj.type !== 'delectable') {return null;}
      let elems = (showAllReviews ? obj.objects : obj.objects.slice(0, InitialNumReviewsToShow)).map((review, ii) => {
        let props = {
          review: review,
          key: ii
        };
        return <InventoryRecordReview {...props} />;
      });
      let header = obj.header ? <h4 className={styles.reviewContainerHeader}>{obj.header}</h4> : null;
      let showAllProps = {
        className: styles.showAllReviewsButton,
        onClick: this.handleOnClickShowAllReviewsButton.bind(this)
      };
      let delectableButton = (
        <div className={styles.textLink}>
          <NavLink className={styles.text} href={inventoryRecord.web_reviews_url}><img className={styles.delectableLogo} src={delectablePng} /> See all reviews and ratings on Delectable <span className={styles.chevron} /></NavLink>
        </div>
      );
      return (
        <div className={styles.reviewContainer} key={i}>
          {header}
          <div className={styles.reviewsList}>{elems}</div>
          {showAllReviews || obj.objects.length <= InitialNumReviewsToShow ? delectableButton : <button {...showAllProps}>See more</button>}
        </div>
      );
    });
  }

  renderSocialMediaLinks() {
    let { inventoryRecord, store } = this.props;
    let encodedUrl = encodeURIComponent(inventoryRecord.getIn(['slug', 'url']));
    let facebookLinkProps = {
      className: styles.link,
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      target: '_blank'
    };
    let twitterLinkProps = {
      className: styles.link,
      href: `https://twitter.com/home?status=${encodedUrl}`,
      target: '_blank'
    };
    let emailLinkProps = {
      className: styles.link,
      href: `mailto:?subject=${inventoryRecord.brand} ${inventoryRecord.name} at ${store.name}&body=${inventoryRecord.slug.url}`,
      target: '_blank'
    };
    return (
      <div className={styles.socialMediaLinks}>
        <a {...facebookLinkProps}><img src={facebookPng} srcSet={facebookSvg} alt={`Share ${inventoryRecord.brand} ${inventoryRecord.name} on Facebook`} /></a>
        <a {...twitterLinkProps}><img src={twitterPng} srcSet={twitterSvg} alt={`Share ${inventoryRecord.brand} ${inventoryRecord.name} on Twitter`} /></a>
        <a {...emailLinkProps}><img src={emailPng} srcSet={emailSvg} alt={`Share ${inventoryRecord.brand} ${inventoryRecord.name} via email`} /></a>
      </div>
    );
  }

  renderJSONLD(inventoryRecord) {
    let json = inventoryRecord.generateSchemaJSON({store: this.props.store});
    return <script type="application/ld+json" dangerouslySetInnerHTML={{__html: JSON.stringify(json)}} />;
  }

  renderAttributes(attributes) {
    let attrs = [];
    attributes.forEach((attr, i) => {
      let content, header;
      switch(attr.type) {
        case 'text_link':
          let { text, url } = attr.object;
          let TextElem = url ? NavLink : 'span';
          header = attr.object.header;
          content = (
            <div className={styles.textLink}>
              {header ? <label className={styles.header}>{header}</label> : null}
              {text ? <TextElem className={styles.text} href={url}>{text} {url ? <span className={styles.chevron} /> : null}</TextElem> : null}
            </div>
          );
          break;
        case 'tag_cloud':
          let { filters } = attr.object;
          header = attr.object.header;
          content = (
            <div className={styles.tagCloud}>
              {header ? <label className={styles.header}>{header}</label> : null}
              {filters.map((filter, ii) => {
                return (
                  <NavLink className={styles.tagCloudFilter} key={ii} href={this.context.buildURLForRoute('search', {filters: [filter.filter]})}>
                    {filter.filter.type === 'tag' ? toTitleCase(filter.filter.value.replace(/-/g, ' ')) : filter.filter.value}
                  </NavLink>
                );
              })}
            </div>
          );
          break;
        default:
          break;
      }
      if(content) {
        attrs.push(<div className={styles.row} key={i}>{content}</div>);
      }
    });
    if(!attrs.length) {return null;}
    return <div className={styles.attributesTable}>{attrs}</div>;
  }

  render() {
    let { inventoryRecord, currentRoute, isNavigateComplete, collection } = this.props;
    let isMobile = this.context.getUserAgent().isMobile;
    let loading = !inventoryRecord; // || !isNavigateComplete;

    if(loading) {
      // Loading state
      return (
        <div className={`${styles.inventoryRecordDetail} ${styles.loading}`}>
          {isMobile ? <div className={styles.photo} /> : null}
          <h1 className={styles.name} />
          <div className={styles.variantPriceLine} />
          <div className={styles.table}>
            {!isMobile ? <div className={styles.photo} /> : null}
            <div className={styles.details}>
              <p className={styles.description} />
            </div>
          </div>
        </div>
      );
    }

    let featuredBadge;
    if(inventoryRecord.featured) {
      featuredBadge = <div className={styles.featuredBadgeWrapper}><span className={styles.featuredBadge}>Staff Pick</span></div>;
    }
    let ratings = this.renderRatings(inventoryRecord.ratings);
    let delectableRatings = null;
    inventoryRecord.ratings.forEach((rating) => {
      if (rating.type == 'delectable') {
        delectableRatings = rating;
      }
    });
    let averageRating = 0;
    let ratingsCount = 0;
    if (delectableRatings && delectableRatings.object) {
      averageRating = delectableRatings.object.average_rating;
      ratingsCount = delectableRatings.object.ratings_count;
    }
    let photoUrl = inventoryRecord.photo_hash.getClosestToTarget(470);
    let imageProps = {
      tag: 'div',
      aspectRatio: (470/450),
      mobileAspectRatio: (343/290),
      targetWidth: 470,
      photoHash: inventoryRecord.photo_hash
    };
    let photo = (
      <div className={styles.photo}>
        <span content={photoUrl} />
        <Image {...imageProps} />
      </div>
    );
    let description = <p className={styles.description}>{inventoryRecord.description}</p>;
    let similarProductsSectionProps = {
      inventoryRecordId: inventoryRecord.id,
      store: this.props.store
    };
    let quantitySelectorProps = {
      showBuyButton: true,
      showSelectRemoveOption: true,
      inventoryRecord: inventoryRecord,
      customer: this.props.customer,
      storeId: this.props.store.id
    };

    return (
      <div className={styles.inventoryRecordDetail}>
        {this.renderJSONLD(inventoryRecord)}
        {isMobile ? photo : null}
        {featuredBadge}
        <h1 className={styles.name}>{inventoryRecord.brand} {inventoryRecord.name}</h1>
        <div className={styles.variantPriceLine}>
          {ratings}
        </div>
        <div className={styles.table}>
          {!isMobile ? photo : null}
          <div className={styles.details}>
            {this.renderPrice()}
            <QuantitySelector {...quantitySelectorProps} />
            {!isMobile ? description : null}
            {this.renderSocialMediaLinks()}
            {this.renderHeartButton()}
            {isMobile ? description : null}
            {this.renderAttributes(inventoryRecord.attributes)}
          </div>
        </div>
        {inventoryRecord.reviews && inventoryRecord.reviews.length ? <div className={styles.separator} /> : null}
        {this.renderReviews(inventoryRecord)}
        {this.props.similarProductCategories && this.props.similarProductCategories.size ? <div className={styles.separator} /> : null}
        <SimilarProductsSection {...similarProductsSectionProps} />
        {this.renderZipCodeModal()}
      </div>
    );
  }
}

export default InventoryRecordDetail;



// WEBPACK FOOTER //
// banquet/components/InventoryRecordDetail/index.js